import React from 'react';
import { Card, Text, Button, Group, Stack, Grid, Title, Badge, ThemeIcon } from '@mantine/core';
import { IconBrandGoogle, IconCheck, IconRotate, IconAlertCircle } from '@tabler/icons-react';
import { motion, AnimatePresence } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

export function ConflictResolutionDashboard() {
  const utils = trpc.useUtils();
  const conflictsQuery = trpc.integrations.google.listConflicts.useQuery();
  const resolveMutation = trpc.integrations.google.resolveConflict.useMutation();

  const handleResolve = async (conflictId: number, action: 'accept' | 'revert') => {
    try {
      await resolveMutation.mutateAsync({ conflictId, action });
      notifications.show({
        message: `Conflict successfully resolved (${action === 'accept' ? 'Google override accepted' : 'reverted to LeadSync local canon'}).`,
        color: 'green',
      });
      void conflictsQuery.refetch();
    } catch (err) {
      notifications.show({
        message: 'Failed to resolve storefront override conflict.',
        color: 'red',
      });
    }
  };

  const getFieldName = (path: string) => {
    if (path === 'title') return 'Storefront Display Name';
    if (path === 'websiteUri') return 'Website URI';
    if (path === 'phoneNumbers.primaryPhoneNumber') return 'Primary Phone Number';
    if (path === 'regularHours') return 'Regular Weekly Operating Hours';
    if (path === 'specialHours') return 'Special / Holiday Operating Hours';
    return path;
  };

  const formatValue = (path: string, valueStr: string | null) => {
    if (!valueStr)
      return (
        <Text size="sm" c="dimmed">
          Not set
        </Text>
      );
    try {
      const parsed = JSON.parse(valueStr);
      if (path === 'regularHours' || path === 'specialHours') {
        return <pre style={{ fontSize: '11px', margin: 0 }}>{JSON.stringify(parsed, null, 2)}</pre>;
      }
      return (
        <Text size="sm" fw={500}>
          {String(parsed)}
        </Text>
      );
    } catch {
      return <Text size="sm">{valueStr}</Text>;
    }
  };

  if (conflictsQuery.isLoading) {
    return (
      <Text size="sm" c="dimmed">
        Loading override conflicts...
      </Text>
    );
  }

  const conflicts = conflictsQuery.data || [];

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Stack gap={2}>
          <Title order={3}>Storefront Data Integrity</Title>
          <Text size="sm" c="dimmed">
            Google crowdsourced updates and local algorithms sometimes override local storefront
            data. Resolve these conflicts below.
          </Text>
        </Stack>
        <Badge color="red" variant="light" size="lg">
          {conflicts.length} Active Conflicts
        </Badge>
      </Group>

      {conflicts.length === 0 ? (
        <Card
          withBorder
          padding="lg"
          radius="md"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Stack align="center" gap="xs">
            <ThemeIcon color="green" size="xl" radius="xl" variant="light">
              <IconCheck size={24} />
            </ThemeIcon>
            <Text fw={600} size="sm">
              No Google Overrides Detected
            </Text>
            <Text size="xs" c="dimmed">
              All connected storefront profiles match the Google registry.
            </Text>
          </Stack>
        </Card>
      ) : (
        <Grid>
          <AnimatePresence>
            {conflicts.map((conflict) => (
              <Grid.Col span={12} key={conflict.id}>
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <Card withBorder padding="md" radius="md">
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Group gap="xs">
                          <IconAlertCircle color="var(--mantine-color-red-6)" size={20} />
                          <Text fw={600} size="sm">
                            {getFieldName(conflict.fieldPath)}
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          Location: {conflict.googleLocation.displayName}
                        </Text>
                      </Group>

                      <Grid gap="md">
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <Card withBorder padding="xs" bg="var(--mantine-color-gray-0)">
                            <Text size="xs" fw={700} c="dimmed" mb={4}>
                              LEADSYNC LOCAL CANON
                            </Text>
                            {formatValue(conflict.fieldPath, conflict.localValue)}
                          </Card>
                        </Grid.Col>

                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <Card
                            withBorder
                            padding="xs"
                            bg="var(--mantine-color-red-0)"
                            style={{ borderColor: 'var(--mantine-color-red-2)' }}
                          >
                            <Group gap={6} mb={4}>
                              <IconBrandGoogle size={14} color="#ea4335" />
                              <Text size="xs" fw={700} c="red">
                                GOOGLE CROWDSOURCED OVERRIDE
                              </Text>
                            </Group>
                            {formatValue(conflict.fieldPath, conflict.googleValue)}
                          </Card>
                        </Grid.Col>
                      </Grid>

                      <Group justify="flex-end" gap="sm" mt="xs">
                        <Button
                          variant="light"
                          color="red"
                          size="xs"
                          leftSection={<IconCheck size={14} />}
                          onClick={() => handleResolve(conflict.id, 'accept')}
                          loading={resolveMutation.isPending}
                        >
                          Accept Google Changes
                        </Button>
                        <Button
                          variant="default"
                          size="xs"
                          leftSection={<IconRotate size={14} />}
                          onClick={() => handleResolve(conflict.id, 'revert')}
                          loading={resolveMutation.isPending}
                        >
                          Revert to App Canon
                        </Button>
                      </Group>
                    </Stack>
                  </Card>
                </motion.div>
              </Grid.Col>
            ))}
          </AnimatePresence>
        </Grid>
      )}
    </Stack>
  );
}
